import { useState, useEffect } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Clock, Loader2, User, Video, Image, Music, Coins, Monitor, CheckCircle, XCircle, ExternalLink, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Search, X } from 'lucide-react'

const PAGE_SIZE = 20

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'In Queue' },
  { value: 'processing', label: 'Generating' },
  { value: 'succeeded', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
] as const

const TYPE_OPTIONS = [
  { value: 'video', label: 'Video' },
  { value: 'image', label: 'Image' },
  { value: 'all', label: 'All' },
] as const

export default function PendingPage() {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(0)
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('video')
  const [projectInput, setProjectInput] = useState('')
  const [projectFilter, setProjectFilter] = useState('')

  useEffect(() => {
    const t = setTimeout(() => {
      setProjectFilter(projectInput.trim())
      setPage(0)
    }, 300)
    return () => clearTimeout(t)
  }, [projectInput])

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const falRequests = useQuery(api.admin.getFalRequests, {
    page,
    pageSize: PAGE_SIZE,
    ...(statusFilter ? { status: statusFilter } : {}),
    type: typeFilter,
    ...(projectFilter ? { project: projectFilter } : {}),
  })

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)
    if (days > 0) {
      return `${days}d ${hours % 24}h ago`
    }
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ago`
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s ago`
    }
    return `${seconds}s ago`
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3" />
            In Queue
          </span>
        )
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Loader2 className="h-3 w-3 animate-spin" />
            Generating
          </span>
        )
      case 'succeeded':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3" />
            Completed
          </span>
        )
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3" />
            Failed
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600">
            {status}
          </span>
        )
    }
  }

  const RequestCard = ({ req }: { req: any }) => {
    const displayImage = req.thumbnailUrl || req.imageUrl
    const isExpanded = expandedIds.has(req._id)

    return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden bg-white hover:bg-neutral-50/50 transition-colors">
      <div className="flex cursor-pointer" onClick={() => toggleExpanded(req._id)}>
        {/* Thumbnail / Input Image */}
        <div className="shrink-0 w-32 h-32 bg-neutral-100 border-r border-neutral-200">
          {displayImage ? (
            <a href={req.videoUrl || displayImage} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
              <img
                src={displayImage}
                alt={req.kind === 'image' ? 'Image' : 'Video'}
                className="w-full h-full object-cover hover:opacity-80 transition-opacity"
              />
            </a>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Image className="h-8 w-8 text-neutral-300" />
            </div>
          )}
        </div>

        {/* Details */}
        <div className="flex-1 p-4">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            {getStatusBadge(req.status)}
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${req.kind === 'image' ? 'bg-purple-100 text-purple-800' : 'bg-cyan-100 text-cyan-800'}`}>
              {req.kind === 'image' ? <Image className="h-3 w-3 mr-1" /> : <Video className="h-3 w-3 mr-1" />}
              {req.kind === 'image' ? 'Image' : 'Video'}
            </span>
            <span className="text-xs font-medium text-neutral-600 bg-neutral-100 px-2 py-0.5 rounded">
              {req.modelId}
            </span>
            {req.aspectRatio && (
              <span className="text-xs text-neutral-500 flex items-center gap-1">
                <Monitor className="h-3 w-3" />
                {req.aspectRatio}
              </span>
            )}
            {req.hasAudio && (
              <span className="text-xs text-neutral-500 flex items-center gap-1">
                <Music className="h-3 w-3" />
                Audio
              </span>
            )}
            {req.credits > 0 && (
              <span className="text-xs text-neutral-500 flex items-center gap-1">
                <Coins className="h-3 w-3" />
                {req.credits}
              </span>
            )}
            <span className={`ml-auto flex items-center justify-center w-6 h-6 rounded-md border transition-colors ${isExpanded ? 'bg-neutral-900 border-neutral-900 text-white' : 'bg-neutral-100 border-neutral-200 text-neutral-500 hover:bg-neutral-200'}`}>
              {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </span>
          </div>

          {/* Prompt */}
          <p className={`text-sm text-neutral-900 mb-3 ${isExpanded ? '' : 'line-clamp-2'}`}>
            {req.prompt || 'No prompt'}
          </p>

          {/* Error message */}
          {req.status === 'failed' && req.errorMessage && (
            <p className={`text-xs text-red-600 mb-2 ${isExpanded ? '' : 'line-clamp-1'}`}>
              Error: {req.errorMessage}
            </p>
          )}

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-4 text-xs text-neutral-500">
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {req.userName || req.userEmail || 'Unknown'}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDuration(Date.now() - req.createdAt)}
            </span>
            {req.duration > 0 && (
              <span>{req.duration}s video</span>
            )}
            {req.externalRequestId && (
              <span className="font-mono text-neutral-400">
                ID: {req.externalRequestId.slice(0, 12)}...
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="border-t border-neutral-200 bg-neutral-50 px-4 py-3 space-y-3">
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs">
            <div>
              <span className="font-medium text-neutral-500">Type</span>
              <p className="text-neutral-900 capitalize">{req.kind}</p>
            </div>
            <div>
              <span className="font-medium text-neutral-500">Model</span>
              <p className="text-neutral-900">{req.modelId}</p>
            </div>
            {req.aspectRatio && (
              <div>
                <span className="font-medium text-neutral-500">Aspect Ratio</span>
                <p className="text-neutral-900">{req.aspectRatio}</p>
              </div>
            )}
            {req.resolution && (
              <div>
                <span className="font-medium text-neutral-500">Resolution</span>
                <p className="text-neutral-900">{req.resolution}</p>
              </div>
            )}
            {req.duration > 0 && (
              <div>
                <span className="font-medium text-neutral-500">Duration</span>
                <p className="text-neutral-900">{req.duration}s</p>
              </div>
            )}
            {req.credits > 0 && (
              <div>
                <span className="font-medium text-neutral-500">Credits</span>
                <p className="text-neutral-900">{req.credits}</p>
              </div>
            )}
            {req.hasAudio && (
              <div>
                <span className="font-medium text-neutral-500">Audio</span>
                <p className="text-neutral-900">Yes</p>
              </div>
            )}
            {req.userPlanInfo && (
              <div>
                <span className="font-medium text-neutral-500">Plan</span>
                <p className="text-neutral-900">{req.userPlanInfo}</p>
              </div>
            )}
            <div>
              <span className="font-medium text-neutral-500">Created</span>
              <p className="text-neutral-900">{new Date(req.createdAt).toLocaleString()}</p>
            </div>
            {req.completedAt && (
              <div>
                <span className="font-medium text-neutral-500">Completed</span>
                <p className="text-neutral-900">{new Date(req.completedAt).toLocaleString()}</p>
              </div>
            )}
            {req.failedAt && (
              <div>
                <span className="font-medium text-neutral-500">Failed</span>
                <p className="text-neutral-900">{new Date(req.failedAt).toLocaleString()}</p>
              </div>
            )}
            {req.externalRequestId && (
              <div className="col-span-2">
                <span className="font-medium text-neutral-500">External Request ID</span>
                <p className="text-neutral-900 font-mono">{req.externalRequestId}</p>
              </div>
            )}
            {req.workflowId && (
              <div>
                <span className="font-medium text-neutral-500">Workflow ID</span>
                <p className="text-neutral-900 font-mono">{req.workflowId}</p>
              </div>
            )}
            {req.clipId && (
              <div>
                <span className="font-medium text-neutral-500">Clip ID</span>
                <p className="text-neutral-900 font-mono">{req.clipId}</p>
              </div>
            )}
          </div>

          {/* Full prompt */}
          {req.prompt && (
            <div>
              <span className="text-xs font-medium text-neutral-500">Full Prompt</span>
              <p className="text-xs text-neutral-900 mt-1 whitespace-pre-wrap bg-white border border-neutral-200 rounded p-2">
                {req.prompt}
              </p>
            </div>
          )}

          {/* Full error message */}
          {req.errorMessage && (
            <div>
              <span className="text-xs font-medium text-neutral-500">Error Message</span>
              <p className="text-xs text-red-600 mt-1 whitespace-pre-wrap bg-white border border-red-200 rounded p-2">
                {req.errorMessage}
              </p>
            </div>
          )}

          {/* Generator params (for image assets) */}
          {req.generatorParams && Object.keys(req.generatorParams).length > 0 && (
            <div>
              <span className="text-xs font-medium text-neutral-500">Generator Params</span>
              <pre className="text-xs text-neutral-900 mt-1 whitespace-pre-wrap bg-white border border-neutral-200 rounded p-2 overflow-x-auto">
                {JSON.stringify(req.generatorParams, null, 2)}
              </pre>
            </div>
          )}

          {/* Metadata */}
          {req.metadata && Object.keys(req.metadata).length > 0 && (
            <div>
              <span className="text-xs font-medium text-neutral-500">Metadata</span>
              <pre className="text-xs text-neutral-900 mt-1 whitespace-pre-wrap bg-white border border-neutral-200 rounded p-2 overflow-x-auto">
                {JSON.stringify(req.metadata, null, 2)}
              </pre>
            </div>
          )}

          {/* URLs */}
          <div className="flex flex-wrap gap-3 text-xs">
            {req.videoUrl && (
              <a href={req.videoUrl} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 flex items-center gap-1">
                <ExternalLink className="h-3 w-3" /> Video URL
              </a>
            )}
            {req.imageUrl && (
              <a href={req.imageUrl} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 flex items-center gap-1">
                <ExternalLink className="h-3 w-3" /> Image URL
              </a>
            )}
            {req.audioUrl && (
              <a href={req.audioUrl} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 flex items-center gap-1">
                <ExternalLink className="h-3 w-3" /> Audio URL
              </a>
            )}
            {req.thumbnailUrl && req.thumbnailUrl !== req.imageUrl && (
              <a href={req.thumbnailUrl} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 flex items-center gap-1">
                <ExternalLink className="h-3 w-3" /> Thumbnail URL
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  )}

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Fal Requests</h1>
        <p className="text-sm text-neutral-500">
          All generation requests
        </p>
      </div>

      {/* Filter toolbar */}
      <div className="flex items-center justify-between gap-4 rounded-lg border border-neutral-200 bg-white p-3 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Type toggle */}
          <div className="flex items-center rounded-lg border border-neutral-200 overflow-hidden">
            {TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => { setTypeFilter(opt.value); setPage(0) }}
                className={`px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  typeFilter === opt.value
                    ? 'bg-neutral-900 text-white'
                    : 'bg-white text-neutral-500 hover:bg-neutral-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Separator */}
          <div className="h-6 w-px bg-neutral-200" />

          {/* Project filter */}
          <div className="relative flex items-center">
            <Search className="absolute left-2.5 h-3.5 w-3.5 text-neutral-400 pointer-events-none" />
            <input
              type="text"
              value={projectInput}
              onChange={(e) => setProjectInput(e.target.value)}
              placeholder="Project shortcode or ID"
              className="pl-8 pr-7 py-1.5 text-sm border border-neutral-200 rounded-md focus:outline-none focus:ring-1 focus:ring-neutral-400 focus:border-neutral-400 w-56"
            />
            {projectInput && (
              <button
                onClick={() => setProjectInput('')}
                className="absolute right-1.5 p-0.5 text-neutral-400 hover:text-neutral-700"
                aria-label="Clear project filter"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Separator */}
          <div className="h-6 w-px bg-neutral-200" />

          {/* Status toggle */}
          <div className="flex items-center gap-1.5">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => { setStatusFilter(opt.value); setPage(0) }}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  statusFilter === opt.value
                    ? opt.value === 'failed'
                      ? 'bg-red-100 text-red-800'
                      : opt.value === 'succeeded'
                        ? 'bg-green-100 text-green-800'
                        : opt.value === 'processing'
                          ? 'bg-blue-100 text-blue-800'
                          : opt.value === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-neutral-100 text-neutral-900'
                    : 'text-neutral-500 hover:bg-neutral-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Pagination */}
        <div className="flex items-center rounded-lg border border-neutral-200 overflow-hidden">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-2.5 py-1.5 hover:bg-neutral-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="px-3 py-1.5 text-sm text-neutral-600 tabular-nums border-x border-neutral-200 min-w-[80px] text-center">
            Page {page + 1}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={!falRequests?.hasMore}
            className="px-2.5 py-1.5 hover:bg-neutral-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Results */}
      {!falRequests ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
        </div>
      ) : falRequests.requests.length === 0 ? (
        <p className="text-sm text-neutral-500 text-center py-12">
          No requests found.
        </p>
      ) : (
        <div className="space-y-3">
          {falRequests.requests.map((req) => (
            <RequestCard key={req._id} req={req} />
          ))}
        </div>
      )}
    </div>
  )
}
